import React, { useState } from "react";
import { Button } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EventDetailsSection from "./EventDetailsSection";
import LocationSection from "./LocationSection";
import TicketsSection from "./TicketsSection";
import SponsorshipSection from "./SponsorshipSection";

type EventDetails = {
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  eventType: string;
  visibility: string;
  featuredImage: string;
  featuredImageFile: File | null;
};

// Form state type
type CreateEventForm = {
  eventDetails: EventDetails;
  location: {
    venue: string;
    streetAddress: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
    isVirtual: boolean;
    virtualEventUrl: string;
  };
  tickets: {
    remainingTickets: string;
    ticketTypes: Array<{
      id: string;
      name: string;
      price: string;
      quantity: string;
      description: string;
    }>;
  };
  sponsorships: {
    presentingSponsor: {
      available: boolean;
      price: string;
      benefits: string;
    };
    featuredSponsors: {
      available: boolean;
      price: string;
      benefits: string;
      options: Array<{
        id: string;
        name: string;
        price: string;
        benefits: string;
      }>;
    };
    supportingSponsors: {
      available: boolean;
      price: string;
      benefits: string;
    };
  };
};

// Initial form state
const initialFormState: CreateEventForm = {
  eventDetails: {
    title: "",
    description: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    eventType: "",
    visibility: "public",
    featuredImage: "",
    featuredImageFile: null,
  },
  location: {
    venue: "",
    streetAddress: "",
    city: "",
    province: "",
    postalCode: "",
    country: "",
    isVirtual: false,
    virtualEventUrl: "",
  },
  tickets: {
    remainingTickets: "",
    ticketTypes: [
      {
        id: `ticket-${Date.now()}`,
        name: "Member",
        price: "",
        quantity: "",
        description: "",
      },
    ],
  },
  sponsorships: {
    presentingSponsor: {
      available: true,
      price: "3000",
      benefits:
        "Top logo placement, speaking opportunity, premium booth location",
    },
    featuredSponsors: {
      available: true,
      price: "1000",
      benefits: "Logo on event materials, booth space, 2 complimentary tickets",
      options: [
        {
          id: `sponsor-${Date.now()}`,
          name: "BALANCE",
          price: "1000",
          benefits:
            "Logo on event materials, booth space, 2 complimentary tickets",
        },
        {
          id: `sponsor-${Date.now() + 1}`,
          name: "MOBILE PLUS",
          price: "750",
          benefits: "Logo on event materials, 1 complimentary ticket",
        },
      ],
    },
    supportingSponsors: {
      available: true,
      price: "250",
      benefits: "Name in program, 1 complimentary ticket",
    },
  },
};

const CreateEvent = () => {
  const [formData, setFormData] = useState<CreateEventForm>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Event details handler
  const handleEventDetailsChange = (
    field: keyof EventDetails,
    value: string
  ) => {
    setFormData({
      ...formData,
      eventDetails: {
        ...formData.eventDetails,
        [field]: value,
      },
    });
  };

  const handleFeaturedImageChange = (file: File | null) => {
    setFormData({
      ...formData,
      eventDetails: {
        ...formData.eventDetails,
        featuredImageFile: file,
        featuredImage: file ? URL.createObjectURL(file) : "",
      },
    });
  };

  // Location handler
  const handleLocationChange = ({
    field,
    value,
  }: {
    field: keyof typeof formData.location;
    value: string | boolean;
  }) => {
    setFormData({
      ...formData,
      location: {
        ...formData.location,
        [field]: value,
      },
    });
  };

  // Tickets handler
  const handleTicketsChange = ({
    field,
    value,
  }: {
    field: keyof Omit<typeof formData.tickets, "ticketTypes">;
    value: string;
  }) => {
    setFormData({
      ...formData,
      tickets: {
        ...formData.tickets,
        [field]: value,
      },
    });
  };

  // Ticket types handler
  const handleAddTicketType = () => {
    const newTicket = {
      id: `ticket-${Date.now()}`,
      name: "",
      price: "",
      quantity: "",
      description: "",
    };

    setFormData({
      ...formData,
      tickets: {
        ...formData.tickets,
        ticketTypes: [...formData.tickets.ticketTypes, newTicket],
      },
    });
  };

  const handleUpdateTicketType = ({
    id,
    field,
    value,
  }: {
    id: string;
    field: keyof (typeof formData.tickets.ticketTypes)[0];
    value: string;
  }) => {
    setFormData({
      ...formData,
      tickets: {
        ...formData.tickets,
        ticketTypes: formData.tickets.ticketTypes.map((ticket) =>
          ticket.id === id ? { ...ticket, [field]: value } : ticket
        ),
      },
    });
  };

  const handleRemoveTicketType = ({ id }: { id: string }) => {
    setFormData({
      ...formData,
      tickets: {
        ...formData.tickets,
        ticketTypes: formData.tickets.ticketTypes.filter(
          (ticket) => ticket.id !== id
        ),
      },
    });
  };

  // Sponsorship handlers
  const handlePresentingSponsorChange = ({
    field,
    value,
  }: {
    field: keyof typeof formData.sponsorships.presentingSponsor;
    value: string | boolean;
  }) => {
    setFormData({
      ...formData,
      sponsorships: {
        ...formData.sponsorships,
        presentingSponsor: {
          ...formData.sponsorships.presentingSponsor,
          [field]: value,
        },
      },
    });
  };

  const handleFeaturedSponsorsChange = ({
    field,
    value,
  }: {
    field: keyof Omit<typeof formData.sponsorships.featuredSponsors, "options">;
    value: string | boolean;
  }) => {
    setFormData({
      ...formData,
      sponsorships: {
        ...formData.sponsorships,
        featuredSponsors: {
          ...formData.sponsorships.featuredSponsors,
          [field]: value,
        },
      },
    });
  };

  const handleAddSponsorOption = () => {
    const newOption = {
      id: `sponsor-${Date.now()}`,
      name: "",
      price: "",
      benefits: "",
    };

    setFormData({
      ...formData,
      sponsorships: {
        ...formData.sponsorships,
        featuredSponsors: {
          ...formData.sponsorships.featuredSponsors,
          options: [
            ...formData.sponsorships.featuredSponsors.options,
            newOption,
          ],
        },
      },
    });
  };

  const handleUpdateSponsorOption = ({
    id,
    field,
    value,
  }: {
    id: string;
    field: keyof (typeof formData.sponsorships.featuredSponsors.options)[0];
    value: string;
  }) => {
    setFormData({
      ...formData,
      sponsorships: {
        ...formData.sponsorships,
        featuredSponsors: {
          ...formData.sponsorships.featuredSponsors,
          options: formData.sponsorships.featuredSponsors.options.map(
            (option) =>
              option.id === id ? { ...option, [field]: value } : option
          ),
        },
      },
    });
  };

  const handleRemoveSponsorOption = ({ id }: { id: string }) => {
    setFormData({
      ...formData,
      sponsorships: {
        ...formData.sponsorships,
        featuredSponsors: {
          ...formData.sponsorships.featuredSponsors,
          options: formData.sponsorships.featuredSponsors.options.filter(
            (option) => option.id !== id
          ),
        },
      },
    });
  };

  const handleSupportingSponsorsChange = ({
    field,
    value,
  }: {
    field: keyof typeof formData.sponsorships.supportingSponsors;
    value: string | boolean;
  }) => {
    setFormData({
      ...formData,
      sponsorships: {
        ...formData.sponsorships,
        supportingSponsors: {
          ...formData.sponsorships.supportingSponsors,
          [field]: value,
        },
      },
    });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Basic validation examples
    if (!formData.eventDetails.title.trim()) {
      newErrors.title = "Event title is required";
    }

    if (!formData.eventDetails.startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!formData.eventDetails.startTime) {
      newErrors.startTime = "Start time is required";
    }

    if (!formData.eventDetails.endDate) {
      newErrors.endDate = "End date is required";
    }

    if (!formData.eventDetails.endTime) {
      newErrors.endTime = "End time is required";
    }

    if (!formData.location.isVirtual && !formData.location.venue.trim()) {
      newErrors.venue = "Venue is required for in-person events";
    }

    if (
      formData.location.isVirtual &&
      !formData.location.virtualEventUrl.trim()
    ) {
      newErrors.virtualEventUrl = "Virtual event URL is required";
    }

    // Add more validation as needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Here you would normally send the data to your API
    console.log("Submitting event data:", formData);

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      alert("Event created successfully!");
    }, 1500);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="max-w-4xl mx-auto">
        <EventDetailsSection
          eventDetails={formData.eventDetails}
          onChange={handleEventDetailsChange}
          onFeaturedImageChange={handleFeaturedImageChange}
          errors={errors}
        />

        <LocationSection
          location={formData.location}
          onChange={handleLocationChange}
          errors={errors}
        />

        <TicketsSection
          tickets={formData.tickets}
          onChange={handleTicketsChange}
          onAddTicketType={handleAddTicketType}
          onUpdateTicketType={handleUpdateTicketType}
          onRemoveTicketType={handleRemoveTicketType}
          errors={errors}
        />

        <SponsorshipSection
          sponsorships={formData.sponsorships}
          onPresentingSponsorChange={handlePresentingSponsorChange}
          onFeaturedSponsorsChange={handleFeaturedSponsorsChange}
          onSupportingSponsorsChange={handleSupportingSponsorsChange}
          onAddSponsorOption={handleAddSponsorOption}
          onUpdateSponsorOption={handleUpdateSponsorOption}
          onRemoveSponsorOption={handleRemoveSponsorOption}
          errors={errors}
        />

        <div className="mt-8 flex justify-center">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={isSubmitting}
            sx={{
              backgroundColor: "#E1A731",
              "&:hover": {
                backgroundColor: "#c78f1e",
              },
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              px: 4,
              py: 1.5,
            }}
          >
            {isSubmitting ? "Creating..." : "Create Event"}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CreateEvent;
